import React, { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material'
import { usePermissions } from '@/domain/permissions/usePermissions'
import { PlatformPermission } from '@/domain/permissions/rbac'
import { useAuth0 } from '@auth0/auth0-react'
export const SubHeader = () => {
  const { user } = useAuth0()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = usePermissions(user?.['https://carringtonlabs.io/appRole'])
  const canViewProfile = hasPermission(PlatformPermission.ViewUser)
  const canManageBankConnections = hasPermission(PlatformPermission.ManageBankConnections)
  const canManageLoanApplication = hasPermission(PlatformPermission.ManageLoanApplication)
  const canManageLoan = hasPermission(PlatformPermission.ManageLoan)

  // Declared in display order and filtered, rather than spliced in at fixed indexes, so a tab the
  // role cannot see does not shift the others out of place.
  const transformedTabs = useMemo(
    () =>
      [
        { label: 'Profile', path: '/profile', visible: canViewProfile },
        { label: 'Income', path: '/income', visible: canViewProfile },
        { label: 'Connections', path: '/connections', visible: canManageBankConnections },
        { label: 'Transactions', path: '/transactions', visible: true },
        { label: 'Eligibility', path: '/eligibility', visible: true },
        { label: 'Applications', path: '/applications', visible: canManageLoanApplication },
        { label: 'Loans', path: '/loans', visible: canManageLoan },
      ]
        .filter(tab => tab.visible)
        .map(({ label, path }) => ({ label, path })),
    [canManageLoanApplication, canManageLoan, canManageBankConnections, canViewProfile]
  )
  const currentTab = transformedTabs.findIndex(tab => location.pathname.startsWith(tab.path))
  const [value, setValue] = useState(currentTab === -1 ? 0 : currentTab)

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    navigate(transformedTabs[newValue].path)
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: '#fff',
          px: isMobile ? 1 : 2,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Sub Header Tabs"
          textColor="secondary"
          TabIndicatorProps={{
            sx: {
              backgroundColor: '#4C4CFF',
            },
          }}
        >
          {transformedTabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              sx={{
                minWidth: 100,
                px: isMobile ? 1 : 2,
                fontSize: isMobile ? '0.85rem' : '1rem',
                textTransform: 'capitalize',
                color: value === index ? '#4C4CFF' : theme.palette.text.secondary,
                '&:hover': {
                  color: '#4C4CFF',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  )
}
